<template>
    <b-overlay :show="isLoading">
        <b-card class="m-4">
            <template #header>
                <div class="text-right">
                    <b-button variant="outline-secondary" @click="$router.go(-1)">
                        <font-awesome-icon icon="angle-left"></font-awesome-icon><span class="ml-2">Regresar</span>
                    </b-button>
                    <b-button variant="primary" :to="{ name: 'PublicationEdit', params: { id: $route.params.id }}" class="ml-2">
                        <font-awesome-icon icon="edit"></font-awesome-icon><span class="ml-2">Modificar</span>
                    </b-button>
                </div>
            </template>

            <b-row class="mb-2">
                <b-col cols="12">
                    <h3>Detalle de publicación</h3>
                </b-col>
            </b-row>

            <b-row class="mb-5 text-left">
                <b-col>
                    <h5><b>Cliente: </b> {{ publication.customer ? publication.customer.legalName : '' }}</h5>
                </b-col>
            </b-row>

            <b-table bordered striped small responsive :items="publicationItems" :fields="fields" ref="publicationTable">
                <template v-slot:cell(folio)="row">
                    {{ row.item.folio }}
                </template>

                <template v-slot:cell(field1)="row" v-if="customFieldsEnabled.includes('field1')">
                    {{ row.item.field1 }}
                </template>

                <template v-slot:cell(field2)="row" v-if="customFieldsEnabled.includes('field2')">
                    {{ row.item.field2 }}
                </template>

                <template v-slot:cell(field3)="row" v-if="customFieldsEnabled.includes('field3')">
                    {{ row.item.field3 }}
                </template>

                <template v-slot:cell(field4)="row" v-if="customFieldsEnabled.includes('field4')">
                    {{ row.item.field4 }}
                </template>

                <template v-slot:cell(field5)="row" v-if="customFieldsEnabled.includes('field5')">
                    {{ row.item.field5 }}
                </template>

                <template v-slot:cell(groups)="row">
                    <template v-if="row.item.isEditing">
                        <v-select label="name" :options="groups" debounce="300" placeholder="Grupos" @search="getCustomerGroups"
                                  @search:focus="getCustomerGroups" :filterable="false" v-model="row.item.groups" multiple>
                            <template slot="no-options">Sin resultados.</template>
                        </v-select>
                    </template>
                    <span v-else>
                        <template v-for="group in row.item.groups">
                            <b-badge variant="primary" class="p-1 ml-1" :key="group.id">{{ group.name }}</b-badge>
                        </template>
                    </span>
                </template>

                <template v-slot:cell(locked)="row">
                    <div class="text-center" v-if="row.item.isEditing">
                        <b-checkbox v-model="row.item.locked"></b-checkbox>
                    </div>
                    <div class="text-center" v-else>
                        <b-badge variant="danger" v-if="row.item.locked">Sí</b-badge>
                        <b-badge variant="success" v-else>No</b-badge>
                    </div>
                </template>

                <template v-slot:cell(status)="row">
                    <div class="text-center">
                        <b-badge variant="info" v-if="row.item.status === 'DRAFT'" class="p-1">Borrador</b-badge>
                        <b-badge variant="success" v-else>Publicado</b-badge>
                    </div>
                </template>

                <template v-slot:cell(files)="row">
                    {{ row.item.files.length }}
                </template>

                <template v-slot:cell(actions)="row">
                    <b-button-group size="sm">
                        <b-button v-b-tooltip.hover.auto @click="row.toggleDetails" variant="outline-primary"
                                  :title="row.detailsShowing ? 'Ocultar archivos' : 'Ver archivos'">
                            <font-awesome-icon :icon="row.detailsShowing ? 'minus' : 'plus'"></font-awesome-icon>
                        </b-button>
                    </b-button-group>
                </template>

                <template v-slot:row-details="row">
                    <template v-if="row.item.files.length > 0">
                        <b-table :items="row.item.files" :fields="fileFields"
                                 striped bordered hover small show-empty responsive style="font-size: .9rem;">
                            <template v-slot:cell(number)="fileRow">{{ fileRow.index + 1 }}</template>

                            <template v-slot:cell(size)="fileRow">{{ formatBytes(fileRow.item.size) }}</template>

                            <template v-slot:cell(actions)="fileRow">
                                <b-button-group>
                                    <b-button v-b-tooltip.hover.auto v-if="canBePreviewed(fileRow.item)" @click="showPreview(fileRow.item)"
                                              variant="outline-primary" title="Vista previa" class="px-1 py-0">
                                        <font-awesome-icon icon="eye"></font-awesome-icon>
                                    </b-button>
                                    <b-button v-b-tooltip.hover.auto @click="download(fileRow.item)"
                                              variant="outline-primary" title="Descargar" class="px-1 py-0">
                                        <font-awesome-icon icon="download"></font-awesome-icon>
                                    </b-button>
                                    <b-button v-b-tooltip.hover.auto @click="openFileLogsModal(fileRow.item)"
                                              variant="outline-primary" title="Ver historial" class="px-1 py-0">
                                        <font-awesome-icon icon="list"></font-awesome-icon>
                                    </b-button>
                                </b-button-group>
                            </template>
                        </b-table>
                    </template>
                    <div class="text-center" v-else> Sin archivos</div>
                </template>
            </b-table>
        </b-card>

        <b-modal id="file-logs-modal" size="lg" title="Historial de descargas" ok-title="Cerrar" ok-only>
            <b-row no-gutters class="text-left">
                <b-col>
                    <b-table :items="fileLogs" :fields="fileLogsfields" empty-text="Sin registros" striped bordered hover small show-empty responsive>
                    </b-table>
                </b-col>
            </b-row>
        </b-modal>
        <b-modal :id="`file-preview-modal`" size="xl" :title="file.name" ok-title="Cerrar" ok-only>
            <iframe class="object" :src="file.previewData" style="width: 100%; height: 650px;"></iframe>`
        </b-modal>
    </b-overlay>
</template>

<script>
import * as constants from '@constants';
import { publicationUrl, fileLogsUrl, fileUrl } from '@routes';
import { saveAs } from 'file-saver';

export default {
    props: {
        settings: Object,
        membership: Object,
        dateFormat: String
    },
    data() {
        return {
            calendar: {
                format: 'yyyy/MM/dd'
            },
            publication: {
                id: 0,
                customer: null,
                publicationDate: '',
                status: 'DRAFT'
            },
            fields: [
                { key: 'folio', label: 'Folio' },
                { key: 'groups', label: 'Grupos' },
                { key: 'locked', label: 'Descarga bloqueada' },
                { key: 'status', label: 'Estatus' },
                { key: 'files', label: 'Archivos', class: 'text-center' },
                { key: 'actions', label: 'Acciones' }
            ],
            fileFields: [
                { key: 'number', label: 'Número', class: 'text-right' },
                { key: 'name', label: 'Nombre', class: 'text-left' },
                { key: 'created', label: 'Fecha de carga', class: 'text-left' },
                { key: 'size', label: 'Tamaño', class: 'text-right' },
                { key: 'createdBy', label: 'Cargado por', class: 'text-left' },
                { key: 'downloadCount', label: 'Cantidad de descargas', class: 'text-right' },
                { key: 'actions', label: 'Acciones', class: 'text-center' }
            ],
            mailData: {
                cc: '',
                subject: 'Nuevos certificados o informes publicados.',
                body: '',
                attachFiles: false
            },
            customFields: Array(5).fill(null),
            publicationItems: [],
            publicationItemsCache: [],
            customers: [],
            groups: [],
            isLoading: false,
            publishType: '',
            publicationToPublish: null,
            sendNotifications: false,
            fileLogs: [],
            fileLogsfields: [
                { key: 'date', label: 'Fecha', sortable: false },
                { key: 'user', label: 'Usuario', sortable: false }
            ],
            file: {
                mimetype: '',
                previewData: ''
            }
        }
    },
    created() {
        this.$parent.loadData().then(() => {
            this.calendar.format = this.dateFormat;

            this.setCustomFields();
        });

        this.loadPublicationData();

        this.$emit('update-breadcrumb-items', [
            {
                text: 'Publicaciones',
                to: { name: 'Publication' }
            },
            {
                text: 'Detalle',
                active: true
            }
        ]);
    },
    methods: {
        loadPublicationData() {
            this.isLoading = true;

            this.axios.get(publicationUrl(), {
                params: {
                    publicationId: this.$route.params.id
                }
            }).then(response => {
                if (response.data.code === constants.CODE_OK) {
                    let publication = response.data.publication;

                    this.publication.id = publication.id;
                    this.publication.publicationDate = publication.publicationDate;
                    this.publication.status = publication.status;
                    this.publication.customer = publication.customer;

                    this.publicationItems = [];

                    for (let item of publication.publicationItems) {
                        item.isEditing = false;
                        item.isSelected = false;
                        item.state1 = null;
                        item.state2 = null;
                        item.state3 = null;
                        item.state4 = null;
                        item.state5 = null;

                        this.publicationItems.push(item);
                    }
                }
            }).catch(

            ).finally(() => {
                this.isLoading = false;
            });
        },
        setCustomFields() {
            for (let i = this.settings.customFields.length - 1; i >= 0; i--) {
                let field = this.settings.customFields[i];

                if (field.enabled) {
                    this.fields.splice(2, 0, {
                        key: `field${i + 1}`,
                        label: field.name
                    });

                    this.customFields.splice(i, 1, {
                        id: field.id.toString(),
                        name: `field${i + 1}`,
                        placeholder: field.name,
                        type: field.type,
                        required: field.required
                    });
                }
            }
        },
        openFileLogsModal(file) {
            if (file.downloadCount > 0) {
                this.axios.get(fileLogsUrl(), {
                    params: {
                        fileId: file.id,
                        action: 'DOWNLOAD'
                    },
                    responseType: 'json'
                }).then(response => {
                    if (response.data.code === 'OK') {
                        this.fileLogs = response.data.logs;
                    }
                });

                this.$bvModal.show('file-logs-modal');
            }
        },
        formatBytes(size) {
            const ratio = 1024, digits = 2;
            let units = ['B', 'KB', 'MB', 'GB'];
            let unit = 0;

            size = parseInt(size);

            while (size > 1024) {
                size /= ratio;
                unit++;
            }

            return `${size.toFixed(digits)} ${units[unit]}`;
        },
        canBePreviewed(file) {
            let validPreviewMimeTypes = ['application/pdf', 'image/png', 'image/jpeg'];

            return validPreviewMimeTypes.includes(file.mimeType);
        },
        showPreview(file) {
            this.axios.get(fileUrl(), {
                params: {
                    fileId: file.id,
                    isPreview: true
                }
            }).then(response => {
                if (response.data.code === constants.CODE_OK) {
                    let blob = this.base64ToBlob(response.data.file, response.data.mimeType);

                    this.file.previewData = URL.createObjectURL(blob);
                    this.file.mimetype = response.data.mimeType;

                    this.$bvModal.show('file-preview-modal');
                } else {
                    this.$root.$bvToast.toast(response.data.message, {
                        variant: 'danger'
                    });
                }
            });
        },
        download(file) {
            this.axios.get(fileUrl(), {
                params: {
                    fileId: file.id,
                    isPreview: false
                },
                responseType: 'blob'
            }).then(response => {
                saveAs(response.data, file.name);
            });
        },
        base64ToBlob(base64, mimeType) {
            const binStr = atob(base64);
            const length = binStr.length;
            const arr = new Uint8Array(length);

            for (let i = 0; i < length; i++) {
                arr[i] = binStr.charCodeAt(i);
            }

            return new Blob([arr], { type: mimeType });
        }
    },
    watch: {
        settings: {
            handler() {
                this.setCustomFields();
            },
            deep: true
        }
    },
    computed: {
        customFieldsEnabled() {
            let fields = [];

            for (let field of this.customFields) {
                if (field) {
                    fields.push(field.name);
                }
            }

            return fields;
        }
    }
}
</script>

<style>

</style>